import { Bundle, EmployeeType } from '@prisma/client';
import { Alert, Collapse, Form, FormItemProps, GlobalToken, Input, theme } from 'antd';

export const JustificationTooltipText = ({ requiredText }: { requiredText?: string | false }) => (
  <>
    <p>
      The <strong>Justification</strong> is used to explain why the worker needs access to the selected bundle items.
      The provided justification will be submitted to AGS and will be visible to the approvers.
    </p>
    <ul>
      {requiredText === false ? null : (
        <li>{requiredText ?? 'Justification may be required if set by the bundle owner.'}</li>
      )}
      <li>Justification must be 500 characters or less</li>
      <li>The characters "&lt;", "&gt;", ";" are not allowed</li>
    </ul>
  </>
);

const justificationInstructions = ({ employeeType, token }: { employeeType?: EmployeeType; token: GlobalToken }) => {
  switch (employeeType) {
    case EmployeeType.CONTINGENT_WORKER:
      return [
        {
          key: '1',
          label: (
            <div>
              <span style={{ color: token.colorPrimary, fontWeight: 'bold' }}>MUST READ</span> Special N2, N3, N5/N4 and
              N7/N6 instructions
            </div>
          ),
          children: (
            <>
              <p>
                Please provide the following information in the justification if requesting access to TSMC advanced
                nodes N2, N3, N5/N4 or N7/N6:
                <ol>
                  <li>The DMS Classic Ticket ID (4/5-digit) and the ticket ID (10-digit)</li>
                  <li>The Product for which you would be working (ex. Nova Lake)</li>
                  <li>The Die(s) for which you would be working (ex. Nova Lake CPU xxx)</li>
                  <li>Your role and task information.</li>
                </ol>
              </p>

              <table style={{ marginBottom: '1em', color: token.colorTextDescription }}>
                <tr>
                  <td>Example:</td>
                  <td style={{ borderBottom: `1px black solid` }}>RUNDA #XXXX, DMS #1602XXXXXX</td>
                  <td>for</td>
                  <td style={{ borderBottom: `1px black solid` }}>Project "XXX"</td>
                  <td>and</td>
                  <td style={{ borderBottom: `1px black solid` }}>specific dies(s)</td>
                  <td>,</td>
                  <td style={{ borderBottom: `1px black solid` }}>Analog Designer</td>
                </tr>
                <tr>
                  <td></td>
                  <td style={{ textAlign: 'center' }}>1</td>
                  <td></td>
                  <td style={{ textAlign: 'center' }}>2</td>
                  <td></td>
                  <td style={{ textAlign: 'center' }}>3</td>
                  <td></td>
                  <td style={{ textAlign: 'center' }}>4</td>
                </tr>
              </table>

              <Alert
                showIcon
                type="info"
                message={
                  <div>
                    Hint: Copy this into the justification and fill in the blanks:
                    <pre>RUNDA #___, DMS #___ for Project "___" and ___, ___</pre>
                  </div>
                }
              />
            </>
          ),
        },
      ];
    case EmployeeType.INTEL:
      return [
        {
          key: '1',
          label: (
            <div>
              <span style={{ color: token.colorPrimary, fontWeight: 'bold' }}>MUST READ</span> Special N2, N3, N5/N4 and
              N7/N6 instructions
            </div>
          ),
          children: (
            <>
              <p>
                Please provide the following information in the justification if requesting access to TSMC advanced
                nodes N2, N3, N5/N4 or N7/N6:
                <ol>
                  <li>The Product for which you would be working (ex. Nova Lake)</li>
                  <li>The Die(s) for which you would be working (ex. Nova Lake CPU xxx)</li>
                  <li>Your role and task information.</li>
                </ol>
              </p>
              <p>
                <span style={{ color: token.colorPrimary, fontWeight: 'bold' }}>For N2</span>, it is mandatory to answer
                these additional questions:
                <ol start={4}>
                  <li>
                    Is the Purpose of your need-to-know of the TSMC N2 confidential information for evaluating or
                    enabling the manufacturing of Intel's products by TSMC? (YES/NO)
                  </li>
                  <li>
                    Does your job require you to do any competitive analysis, marketing, sales or operations for use by
                    Intel Foundry? (YES/NO)
                  </li>
                  <li>Is your job function related to Process Technology Definition or Development? (YES/NO)</li>
                  <li>Provide the end date for access to be removed (MM/DD/YYYY)</li>
                </ol>
              </p>

              <table style={{ marginBottom: '1em', color: token.colorTextDescription }}>
                <tr>
                  <td>Example:</td>
                  <td style={{ borderBottom: `1px black solid` }}>Project "XXX"</td>
                  <td>and</td>
                  <td style={{ borderBottom: `1px black solid` }}>specific dies(s)</td>
                  <td>,</td>
                  <td style={{ borderBottom: `1px black solid` }}>Analog Designer</td>
                  <td>.</td>
                  <td style={{ borderBottom: `1px black solid` }}>(include N2 answers if this is for N2 access)</td>
                </tr>
                <tr>
                  <td></td>
                  <td style={{ textAlign: 'center' }}>1</td>
                  <td></td>
                  <td style={{ textAlign: 'center' }}>2</td>
                  <td></td>
                  <td style={{ textAlign: 'center' }}>3</td>
                  <td></td>
                  <td style={{ textAlign: 'center' }}>4-7</td>
                </tr>
              </table>

              <Alert
                showIcon
                type="info"
                message={
                  <div>
                    Hint: Copy this into the justification and fill in the blanks:
                    <pre>
                      Project: "__", Die/IP: "_", role/task: "_/__" <br />
                    </pre>
                    Or for N2:
                    <pre>
                      Project: "__", Die/IP: "_", role/task: "_/__" <br />
                      Evaluating or enabling the manufacturing of Intel's products by TSMC? (YES/NO)
                      <br />
                      Does your job require competitive analysis, marketing, sales or operations for use by Intel
                      Foundry? (YES/NO)
                      <br />
                      Is your job function related to Process Technology Definition or Development? (YES/NO)
                      <br />
                      Provide the end date for access to be removed (MM/DD/YYYY)
                      <br />
                    </pre>
                  </div>
                }
              />
            </>
          ),
        },
      ];
    default:
      return undefined;
  }
};

export const useJustificationFormItem = (bundle?: Bundle) => {
  const { token } = theme.useToken();
  const getProps = (): FormItemProps => ({
    children: (
      <>
        {/**
         * collapse should not be rendered until we have the bundle data otherwise the defaultActiveKey
         * will be set to undefined and changing it will have no affect (since it's the default)
         */}
        {bundle ? (
          <Collapse
            defaultActiveKey={bundle?.justificationRequired ? ['1'] : undefined}
            items={justificationInstructions({ employeeType: bundle?.employeeType, token })}
            style={{ marginBottom: '1em' }}
          />
        ) : undefined}

        <Form.Item
          name="justification"
          noStyle
          rules={[
            { required: bundle?.justificationRequired, message: 'Justification is required.' },
            { max: 6000, message: 'Justification must be less than 6000 characters.' },
            { pattern: new RegExp(/^([^<>;]+)$/), message: 'The characters "<", ">", ";" are not allowed.' },
          ]}
        >
          <Input.TextArea rows={10} maxLength={6000} showCount />
        </Form.Item>
      </>
    ),
    hasFeedback: true,
    label: 'Justification',
    required: bundle?.justificationRequired,
    tooltip: (
      <JustificationTooltipText requiredText={bundle?.justificationRequired ? 'Justification is required.' : false} />
    ),
  });

  return { getProps };
};
